import type { WorkflowCaseConflict } from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'
import type { TFunction } from 'i18next'

import { convertAreaLabel } from '~/models/area'
import { formatDateDisplay, fromProtoDate } from '~/models/dateTime'

/**
 * 排他衝突 (spec AC-003a) の error detail を人読みメッセージへ整形する純関数。
 * spec 指定の箇条書き行フォーマット
 * 「BG: {BG 名 or 「BG なし」} ({BG 所属エリア名})、対象日: {YYYY/MM/DD}」
 * で衝突 cell を全列挙する (issue #5219: エリアは排他キーから除去され、行の
 * 表示は BG 名 + その所属エリアのみになった。既存 ID は spec に無いため出さない
 * — `existingWorkflowId` は proto に残るが frontend では使わない)。
 *
 * BG 軸を持たないケース同士の衝突 (= bgCode 未設定) は「BG: BG なし、対象日: …」
 * とエリア括弧を伴わずに表記する (workflow-bg-code-derivation design、
 * 画面状態マトリクス)。「BG なし」判定は bgCode の有無で行う (bgName は表示専用
 * — rolling update 窓で旧 backend が bg_name を返さない場合でも bgCode があれば
 * 「BG あり」衝突として扱い、表示名は bgCode で代替する)。
 */
export function buildConflictMessage(
  conflicts: WorkflowCaseConflict[],
  t: TFunction,
): string {
  const header = t('page:workflowCases.serverError.conflict')
  const lines = conflicts.map((c) => {
    const dateStr =
      c.targetDate !== undefined
        ? formatDateDisplay(fromProtoDate(c.targetDate))
        : '-'
    const line =
      c.bgCode === undefined
        ? t('page:workflowCases.serverError.conflictLineNoBg', {
            date: dateStr,
          })
        : t('page:workflowCases.serverError.conflictLineWithBg', {
            bgName: c.bgName !== '' ? c.bgName : c.bgCode,
            area: convertAreaLabel(c.areaCode),
            date: dateStr,
          })
    return `・${line}`
  })
  return `${header}\n${lines.join('\n')}`
}
